/**
 * Loading State Management Store
 * Manages loading indicators and progress feedback across the application
 */
import { create } from 'zustand';

// Loading operation types
export const LOADING_TYPES = {
  VIEWPORT: 'viewport',
  FILTER: 'filter',
  SEARCH: 'search',
  VALIDATION: 'validation',
  RETRY: 'retry'
};

// Loading state interface
const initialLoadingState = {
  isLoading: false,
  loadingType: null,
  progress: 0,
  statusMessage: '',
  startTime: null,
  cancellable: false,
  operations: new Map() // Track multiple concurrent operations
};

export const useLoadingStore = create((set, get) => ({
  ...initialLoadingState,

  /**
   * Start a loading operation
   */
  startLoading: (type, options = {}) => {
    const {
      statusMessage = 'Loading...',
      cancellable = false,
      estimatedDuration = null
    } = options;

    const operationId = `${type}-${Date.now()}`;
    const startTime = new Date();

    set((state) => {
      const newOperations = new Map(state.operations);
      newOperations.set(operationId, {
        type,
        statusMessage,
        startTime,
        cancellable,
        estimatedDuration,
        progress: 0
      });

      return {
        ...state,
        isLoading: true,
        loadingType: type,
        progress: 0,
        statusMessage,
        startTime,
        cancellable,
        operations: newOperations
      };
    });

    return operationId;
  },

  /**
   * Update progress for a loading operation
   */
  updateProgress: (operationId, progress, statusMessage) => {
    set((state) => {
      const newOperations = new Map(state.operations);
      const operation = newOperations.get(operationId);

      if (operation) {
        operation.progress = progress;
        if (statusMessage) {
          operation.statusMessage = statusMessage;
        }
        newOperations.set(operationId, operation);

        // If this is the current primary operation, update main state
        const isPrimaryOperation = state.loadingType === operation.type;

        return {
          ...state,
          operations: newOperations,
          ...(isPrimaryOperation && {
            progress,
            statusMessage: statusMessage || state.statusMessage
          })
        };
      }

      return state;
    });
  },

  /**
   * Stop a specific loading operation
   */
  stopLoading: (operationId) => {
    set((state) => {
      const newOperations = new Map(state.operations);
      const operation = newOperations.get(operationId);

      if (!operation) return state;

      newOperations.delete(operationId);

      // If no more operations, clear loading state
      if (newOperations.size === 0) {
        return {
          ...state,
          isLoading: false,
          loadingType: null,
          progress: 0,
          statusMessage: '',
          startTime: null,
          cancellable: false,
          operations: newOperations
        };
      }

      // If this was the primary operation, switch to next operation
      const wasPrimaryOperation = state.loadingType === operation.type;
      if (wasPrimaryOperation) {
        const nextOperation = Array.from(newOperations.values())[0];
        return {
          ...state,
          loadingType: nextOperation.type,
          progress: nextOperation.progress,
          statusMessage: nextOperation.statusMessage,
          startTime: nextOperation.startTime,
          cancellable: nextOperation.cancellable,
          operations: newOperations
        };
      }

      return {
        ...state,
        operations: newOperations
      };
    });
  },

  /**
   * Stop all loading operations of a specific type
   */
  stopLoadingByType: (type) => {
    set((state) => {
      const newOperations = new Map();
      let shouldUpdatePrimary = false;

      // Remove operations of specified type
      state.operations.forEach((operation, operationId) => {
        if (operation.type !== type) {
          newOperations.set(operationId, operation);
        } else if (state.loadingType === type) {
          shouldUpdatePrimary = true;
        }
      });

      // If no more operations, clear loading state
      if (newOperations.size === 0) {
        return {
          ...state,
          isLoading: false,
          loadingType: null,
          progress: 0,
          statusMessage: '',
          startTime: null,
          cancellable: false,
          operations: newOperations
        };
      }

      // If we removed the primary operation, switch to next
      if (shouldUpdatePrimary) {
        const nextOperation = Array.from(newOperations.values())[0];
        return {
          ...state,
          loadingType: nextOperation.type,
          progress: nextOperation.progress,
          statusMessage: nextOperation.statusMessage,
          startTime: nextOperation.startTime,
          cancellable: nextOperation.cancellable,
          operations: newOperations
        };
      }

      return {
        ...state,
        operations: newOperations
      };
    });
  },

  /**
   * Clear all loading operations
   */
  clearAllLoading: () => {
    set(() => ({
      ...initialLoadingState,
      operations: new Map()
    }));
  },

  /**
   * Get loading duration in milliseconds
   */
  getLoadingDuration: () => {
    const { startTime } = get();
    return startTime ? Date.now() - startTime.getTime() : 0;
  },

  /**
   * Get loading operations by type
   */
  getOperationsByType: (type) => {
    const { operations } = get();
    const result = [];
    operations.forEach((operation, operationId) => {
      if (operation.type === type) {
        result.push({ operationId, ...operation });
      }
    });
    return result;
  },

  /**
   * Check if specific type is loading
   */
  isTypeLoading: (type) => {
    const { operations } = get();
    for (const operation of operations.values()) {
      if (operation.type === type) {
        return true;
      }
    }
    return false;
  },

  /**
   * Get active operations count
   */
  getActiveOperationsCount: () => {
    const { operations } = get();
    return operations.size;
  },

  /**
   * Cancel a loading operation if cancellable
   */
  cancelOperation: (operationId) => {
    const { operations } = get();
    const operation = operations.get(operationId);

    if (operation && operation.cancellable) {
      get().stopLoading(operationId);
      return true;
    }
    return false;
  },

  /**
   * Get estimated time remaining for current operation
   */
  getEstimatedTimeRemaining: () => {
    const { operations, loadingType, startTime, progress } = get();

    if (!startTime || !loadingType || progress === 0) {
      return null;
    }

    // Find current operation
    const currentOperation = Array.from(operations.values())
      .find(op => op.type === loadingType);

    if (!currentOperation || !currentOperation.estimatedDuration) {
      return null;
    }

    const elapsed = Date.now() - startTime.getTime();
    const estimatedTotal = currentOperation.estimatedDuration;
    const progressRatio = progress / 100;

    if (progressRatio > 0) {
      const estimatedTotalFromProgress = elapsed / progressRatio;
      return Math.max(0, estimatedTotalFromProgress - elapsed);
    }

    return Math.max(0, estimatedTotal - elapsed);
  }
}));

/**
 * Loading operation manager class for easier usage
 */
export class LoadingManager {
  constructor() {
    this.store = useLoadingStore;
  }

  /**
   * Create a loading operation with automatic progress tracking
   */
  async withLoading(type, operation, options = {}) {
    const operationId = this.store.getState().startLoading(type, options);

    try {
      // If operation is a promise, track it
      if (operation && typeof operation.then === 'function') {
        const result = await operation;
        this.store.getState().stopLoading(operationId);
        return result;
      }

      // If operation is a function, execute it
      if (typeof operation === 'function') {
        const result = await operation((progress, message) => {
          this.store.getState().updateProgress(operationId, progress, message);
        });
        this.store.getState().stopLoading(operationId);
        return result;
      }

      // Direct value
      this.store.getState().stopLoading(operationId);
      return operation;

    } catch (error) {
      this.store.getState().stopLoading(operationId);
      throw error;
    }
  }

  /**
   * Start a manual loading operation
   */
  start(type, options = {}) {
    return this.store.getState().startLoading(type, options);
  }

  /**
   * Update progress for a manual loading operation
   */
  updateProgress(operationId, progress, message) {
    this.store.getState().updateProgress(operationId, progress, message);
  }

  /**
   * Stop a manual loading operation
   */
  stop(operationId) {
    this.store.getState().stopLoading(operationId);
  }

  /**
   * Quick access methods for common loading types
   */
  viewport = {
    start: (options = {}) => this.start(LOADING_TYPES.VIEWPORT, {
      statusMessage: 'Loading mines in area...',
      cancellable: true,
      estimatedDuration: 3000,
      ...options
    }),
    stop: (operationId) => this.stop(operationId)
  };

  filter = {
    start: (options = {}) => this.start(LOADING_TYPES.FILTER, {
      statusMessage: 'Applying filters...',
      cancellable: true,
      estimatedDuration: 2000,
      ...options
    }),
    stop: (operationId) => this.stop(operationId)
  };

  search = {
    start: (options = {}) => this.start(LOADING_TYPES.SEARCH, {
      statusMessage: 'Searching mines...',
      cancellable: true,
      estimatedDuration: 1500,
      ...options
    }),
    stop: (operationId) => this.stop(operationId)
  };
}

// Export singleton instance
export const loadingManager = new LoadingManager();